import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { parseFloatSafe } from "@omni/utils/number.utils";
import queryString from "query-string";
import { useCallback, useEffect, useMemo, useState } from "react";

import * as CalculatorStepTracker from "@/services/tracking/trackers/tools/calculators/calculatorStep.tracker";
import { CALCULATOR_KEYS } from "@/services/tracking/trackers/tools/calculators/config";

import {
    CorrugatedSheetShoppingList,
    OndulineColor,
    OndulineProductAccessory,
    OndulineProductTileModel,
    ProductLine,
    ProductLineType
} from "./corrugatedSheetCalculator.types";
import { CorrugatedSheetsCalculationConfiguration } from "./corrugatedSheetsCalculation.config";

type CalculateResultCallback = (props: {
    height?: number;
    width?: number;
    length?: number;
    isTwoSidedRoof: boolean;
    activeAcc: OndulineProductTileModel[];
    color?: OndulineColor;
}) => CorrugatedSheetShoppingList;

type CalculateSheetsCallback = (
    surface: number,
    slope: number,
    productLine: ProductLineType,
    sidesMultiplier: number,
    accessories: Array<OndulineProductTileModel>
) => { amount: number; hasSlopeWarning: boolean; fixtures: number };

type useCorrugatedSheetCalculationProps = {};

const filterAccessories = (
    accessory: Product<ProductFieldSet.ProductTile>,
    ondulineAccessories: OndulineProductAccessory[],
    activeAcc: OndulineProductTileModel[],
    isSingleSided: boolean
) => {
    const acc = ondulineAccessories.find((ao) => ao.sku === accessory.sku)!;
    if (acc.condition) {
        return acc.condition(activeAcc, isSingleSided);
    }

    return true;
};

export const ConfigurationSkuList = Object.values(
    CorrugatedSheetsCalculationConfiguration
).reduce(
    (acc, cfg) => {
        return Array.from(
            new Set([
                ...acc,
                ...cfg.sheets.map((s) => s.sku),
                ...cfg.fixtures.map((s) => s.sku),
                ...cfg.accessories.map((s) => s.sku)
            ])
        );
    },
    [] as Array<string>
);

export const useCorrugatedSheetCalculation = (
    _props: useCorrugatedSheetCalculationProps = {}
) => {
    const [activeAcc, setActiveAcc] = useState<Array<OndulineProductTileModel>>(
        []
    );
    const [height, setHeight] = useState<string>("0");
    const [width, setWidth] = useState<string>("0");
    const [length, setLength] = useState<string>("0");
    const [isTwoSidedRoof, setIsTwoSidedRoof] = useState<boolean>(false);
    const [color, setColor] = useState<OndulineColor>();
    const [productLine, setProductLine] = useState<ProductLineType>(
        ProductLine.OndulineOndalux
    );
    const [result, setResult] = useState<CorrugatedSheetShoppingList>({
        surface: 0,
        slope: 0,
        hasSlopeWarning: false,
        accessories: []
    });
    const [products, setProductData] = useState<
        Array<Product<ProductFieldSet.ProductTile>>
    >([]);

    useEffect(() => {
        const q = queryString.parse(location.search, {
            parseNumbers: true,
            parseBooleans: true
        });

        setHeight(q.roofHeight?.toString() || "0");
        setWidth(q.roofWidth?.toString() || "0");
        setLength(q.rootLength?.toString() || "0");
        setProductLine(
            (q.productLine as ProductLineType) || ProductLine.OndulineOndalux
        );
        setIsTwoSidedRoof(!!(q.isTwoSidedRoof as boolean));
        setColor(
            q.color
                ? (q.color as OndulineColor)
                : CorrugatedSheetsCalculationConfiguration[
                      ProductLine.Onduvilla
                  ].sheets.at(0)?.color
        );
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const calculateSheets = useCallback<CalculateSheetsCallback>(
        (surface, slope, productLine, _sidesMultiplier, accessories) => {
            const config =
                CorrugatedSheetsCalculationConfiguration[productLine];
            const currentSlope = config.slopes.find((s) => {
                const below = s.below || 0;
                const above = s.above || 0;

                let result = true;
                if (below && !above) {
                    result = slope <= below;
                }
                if (below && above) result = slope <= below && slope > above;
                if (above && !below) result = slope > above;

                return result;
            });

            if (!currentSlope?.division) {
                return { amount: 0, hasSlopeWarning: false, fixtures: 0 };
            }

            const amount = Math.ceil(1.15 * (surface / currentSlope.division));
            const slopeFixtures = amount * currentSlope.fixtures;
            const accFixtures = activeAcc.reduce((acc, curr) => {
                const productAcc = accessories.find((a) => a.sku === curr.sku)!;
                return (
                    acc +
                    Math.ceil(
                        productAcc.amount *
                            productAcc.requiredFixtures *
                            productAcc.twoSideMultiplier
                    )
                );
            }, 0);

            return {
                amount: amount,
                hasSlopeWarning: !!currentSlope.warning,
                fixtures: slopeFixtures + accFixtures
            };
        },
        [width, length, productLine, isTwoSidedRoof, activeAcc]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const calculateResult = useCallback<CalculateResultCallback>(
        ({ height, width, length, isTwoSidedRoof, activeAcc, color }) => {
            const sidesMultiplier = isTwoSidedRoof ? 2 : 1;

            const surface = (width || 0) * (length || 0) * sidesMultiplier;
            const slope =
                (width || 0) > 0 ? ((height || 0) / (width || 0)) * 100 : 0;

            const productLineConfig =
                CorrugatedSheetsCalculationConfiguration[productLine];

            const ondulineAccessories = productLineConfig.accessories.filter(
                (a) => a.color === color || a.color === undefined
            );

            const accProducts = products.filter((p) =>
                ondulineAccessories.some((a) => a.sku === p.sku)
            );
            const accessories = accProducts
                .filter((a) =>
                    filterAccessories(
                        a,
                        ondulineAccessories,
                        activeAcc,
                        !isTwoSidedRoof
                    )
                )
                .map((a) => {
                    const acc = ondulineAccessories.find(
                        (oa) => oa.sku === a.sku
                    )!;

                    const amount = Math.ceil(
                        acc?.amountFormula(length || 0, width || 0) || 0
                    );
                    return {
                        ...a,
                        amount,
                        requiredFixtures: acc.requiredFixtures,
                        twoSideMultiplier: acc.twoSideMultiplier || 0,
                        dependsOn: acc.dependsOn || ""
                    } as OndulineProductTileModel;
                });

            const onudlineSheet = productLineConfig.sheets.find(
                (s) => s.color === color
            );

            const sheetsResult = calculateSheets(
                surface / 10_000, // in m2 ipv cm2
                slope / 100, // in decimalen ipv %
                productLine,
                sidesMultiplier,
                accessories
            );

            const ondulineFixture = productLineConfig.fixtures.find(
                (f) => f.color === color
            );

            const sheet = products.find((p) => p.sku === onudlineSheet?.sku);
            const fixture = products.find(
                (p) => p.sku === ondulineFixture?.sku
            );

            return {
                surface,
                slope,
                productLine: productLine,
                hasSlopeWarning: sheetsResult.hasSlopeWarning,
                sheet: {
                    ...sheet,
                    amount: Math.ceil(
                        sheetsResult.amount /
                            (onudlineSheet?.itemsPerPackage || 1)
                    ),
                    itemsPerPackage: onudlineSheet?.itemsPerPackage || 1
                } as OndulineProductTileModel,
                fixture: {
                    ...fixture,
                    amount: Math.ceil(
                        sheetsResult.fixtures /
                            (ondulineFixture?.itemsPerPackage || 1)
                    ),
                    itemsPerPackage: ondulineFixture?.itemsPerPackage || 1
                } as OndulineProductTileModel,
                accessories
            };
        },
        [height, width, length, productLine, isTwoSidedRoof, color, activeAcc]
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    const hasValidResult = useMemo(() => {
        return (
            result.surface > 0 &&
            result.slope > 0 &&
            !!productLine &&
            color !== undefined &&
            (result.sheet?.amount || 0) > 0
        );
    }, [result]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        setResult(
            calculateResult({
                height: parseFloatSafe(height, 0),
                width: parseFloatSafe(width, 0),
                length: parseFloatSafe(length, 0),
                isTwoSidedRoof,
                activeAcc,
                color
            })
        );
        CalculatorStepTracker.trackStart(CALCULATOR_KEYS.corrugateSheet);
    }, [height, width, length, productLine, isTwoSidedRoof, activeAcc, color]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        setColor(
            CorrugatedSheetsCalculationConfiguration[
                ProductLine.Onduvilla
            ].sheets.at(0)?.color
        );
    }, [productLine]);

    const toggleAcc = (acc: OndulineProductTileModel) => {
        setActiveAcc((state) => {
            const hasAcc = state.some((s) => s.sku === acc.sku);
            return hasAcc
                ? state.filter((s) => s.sku !== acc.sku)
                : [...state, acc];
        });
    };

    return {
        setHeight,
        setWidth,
        setLength,
        setProductLine,
        setIsTwoSidedRoof,
        setColor,
        setProductData,
        toggleAcc,
        height,
        width,
        length,
        productLine,
        isTwoSidedRoof,
        result,
        hasValidResult,
        color,
        activeAcc
    };
};
